
import React, { useState, useMemo, useEffect } from 'react';
import { WallConfig, PresetRatio, CalculationResult } from './types';
import Sidebar from './components/Sidebar';
import Visualizer from './components/Visualizer';
import MetricsGrid from './components/MetricsGrid';
import Changelog from './components/Changelog';
import CookieBanner from './components/CookieBanner';
import { trackPageView, trackEvent } from './analytics';

const initialConfig: WallConfig = {
  cabinetsWide: 10,
  cabinetsHigh: 6,
  pixelsWide: 128,
  pixelsHigh: 128,
  targetRatio: PresetRatio.RATIO_16_9,
  customRatio: "16:9",
  customDecimal: "1.778",
  lastEditedCustom: 'ratio',
  cabinetSizePreset: "500x500",
  pixelPitchPreset: "3.9",
  showDecimals: true
};

const App: React.FC = () => {
  const [config, setConfig] = useState<WallConfig>(initialConfig);

  // Ensure the page title is always set correctly and track view
  useEffect(() => {
    document.title = "Hartley Video - LED Vision";
    trackPageView("Main Tool View");
  }, []);

  const results = useMemo((): CalculationResult => {
    const { cabinetsWide, cabinetsHigh, pixelsWide, pixelsHigh, targetRatio, customRatio, customDecimal, lastEditedCustom, cabinetSizePreset } = config;
    
    const wallW = cabinetsWide * pixelsWide;
    const wallH = cabinetsHigh * pixelsHigh;
    const wallRatio = wallW / wallH || 1;

    // Physical dimensions
    const [w, h] = cabinetSizePreset.split('x').map(Number);
    const physicalWidthM = ((cabinetsWide * w) / 1000);
    const physicalHeightM = ((cabinetsHigh * h) / 1000);

    const parseRatioParts = (input: string): [number, number] => {
      if (input.includes(':')) {
        const parts = input.split(':');
        const rw = parseFloat(parts[0]);
        const rh = parseFloat(parts[1]);
        if (!isNaN(rw) && !isNaN(rh) && rh !== 0) {
          return [rw, rh];
        }
      }
      const dec = parseFloat(input);
      return !isNaN(dec) ? [dec, 1] : [16, 9];
    };

    let targetW = 16;
    let targetH = 9;

    if (targetRatio === PresetRatio.RATIO_16_9) [targetW, targetH] = [16, 9];
    else if (targetRatio === PresetRatio.RATIO_9_16) [targetW, targetH] = [9, 16];
    else if (targetRatio === PresetRatio.RATIO_21_9) [targetW, targetH] = [21, 9];
    else if (targetRatio === PresetRatio.RATIO_4_3) [targetW, targetH] = [4, 3];
    else if (targetRatio === PresetRatio.CUSTOM) {
      if (lastEditedCustom === 'ratio') {
        [targetW, targetH] = parseRatioParts(customRatio);
      } else {
        targetW = parseFloat(customDecimal) || 1.778;
        targetH = 1;
      }
    }

    const targetDecimal = targetW / targetH;

    // Fit Mode: Maintains aspect ratio, fits inside wall (Letterbox/Pillarbox)
    let fitW, fitH, offsetX = 0, offsetY = 0;
    if (wallRatio > targetDecimal) {
      fitH = wallH;
      fitW = wallH * targetDecimal;
      offsetX = (wallW - fitW) / 2;
    } else {
      fitW = wallW;
      fitH = wallW / targetDecimal;
      offsetY = (wallH - fitH) / 2;
    }

    // Fill Mode: Maintains aspect ratio, scales to cover wall (Zoom/Crop)
    let fillW, fillH, lostW = 0, lostH = 0;
    if (wallRatio > targetDecimal) {
      // Wall is wider than target. Scale width to cover width.
      fillW = wallW;
      fillH = wallW / targetDecimal;
      lostH = fillH - wallH;
    } else {
      // Wall is taller than target. Scale height to cover height.
      fillH = wallH;
      fillW = wallH * targetDecimal;
      lostW = fillW - wallW;
    }
    const zoom = (fillW / (targetDecimal * wallH || 1)) * 100;

    const diff = Math.abs(wallRatio - targetDecimal);
    const accuracy = Math.max(0, 100 - (diff / (targetDecimal || 1)) * 100);

    return {
      totalWidth: wallW,
      totalHeight: wallH,
      actualRatio: wallRatio,
      targetDecimal,
      targetW,
      targetH,
      physicalWidthM,
      physicalHeightM,
      normW_x: targetW / wallRatio,
      normX_H: wallRatio * targetH,
      fitMode: { width: fitW, height: fitH, offsetX, offsetY },
      fillMode: { width: fillW, height: fillH, zoom, lostW, lostH },
      accuracy
    };
  }, [config]);

  const handleEmailClick = () => {
    trackEvent('click_support_email');
    const user = 'app';
    const domain = 'hartleyvideo.com';
    window.location.href = `mailto:${user}@${domain}`;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col md:overflow-hidden">
      {/* Brand Header */}
      <div className="bg-black border-b border-slate-800 py-3 px-6 flex items-center justify-between shrink-0">
        <a 
          href="https://www.hartleyvideo.com" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="flex items-center gap-4 group transition-opacity hover:opacity-90"
        >
          <img 
            src="https://i.ibb.co/fYXW2c9t/HARTLEY-VIDEO-LOGO-transp-crop.png" 
            alt="Hartley Video" 
            className="h-10 md:h-12 w-auto object-contain"
          />
          <div className="h-8 w-px bg-slate-800 hidden md:block"></div>
          <p className="text-[10px] md:text-xs font-medium text-slate-400 italic tracking-wide hidden sm:block">
            Meet the man behind the app...
          </p>
        </a>
        <div className="flex items-center gap-6">
          <a 
            href="https://www.hartleyvideo.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-slate-500 hover:text-[#ff8000] transition-colors"
          >
            HARTLEY VIDEO
          </a>
        </div>
      </div>

      {/* Beta Banner */}
      <div className="bg-slate-900 border-b border-slate-800 py-1.5 px-4 text-center shrink-0">
        <div className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] flex items-center justify-center gap-3">
          <span className="bg-[#ff8000] text-slate-950 px-1.5 py-0.5 rounded text-[8px] font-black leading-none">BETA</span>
          <span>
            This app is in beta. Bug reports: 
            <button 
              onClick={handleEmailClick}
              className="text-[#ff8000] hover:text-[#ff9933] ml-1.5 transition-colors underline underline-offset-2 uppercase font-bold tracking-[0.15em]"
            >
              app@hartleyvideo.com
            </button>
          </span>
        </div>
      </div>

      {/* Mobile Top Bar */}
      <header className="md:hidden bg-slate-900 border-b border-slate-800 p-4 sticky top-0 z-50 shadow-md">
        <div className="flex items-start gap-3">
          <span className="w-1.5 h-6 bg-[#ff8000] rounded-full block shrink-0 mt-0.5"></span>
          <div>
            <h1 className="text-sm font-bold tracking-tight text-white uppercase leading-none">
              HARTLEY VIDEO - LED VISION
            </h1>
            <p className="text-[10px] text-slate-400 mt-1 uppercase font-medium tracking-wide leading-tight">
              Professional Aspect Ratio & Pixel Calculator for LED Video Walls
            </p>
          </div>
        </div>
      </header>

      <div className="flex flex-col md:flex-row flex-1 md:overflow-hidden">
        <Sidebar config={config} setConfig={setConfig} results={results} />

        <main className="flex-1 flex flex-col p-4 md:p-8 md:overflow-y-auto space-y-8 scrolling-touch">
          <header className="hidden md:flex flex-row flex-wrap justify-between items-center gap-x-12 gap-y-4">
            <div className="min-w-fit">
              <h1 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2 uppercase whitespace-nowrap">
                <span className="w-2 h-8 bg-[#ff8000] rounded-full block"></span>
                HARTLEY VIDEO - LED VISION
              </h1>
              <p className="text-slate-400 text-sm mt-1 whitespace-nowrap lg:whitespace-normal xl:whitespace-nowrap">
                Professional Aspect Ratio & Pixel Calculator for LED Video Walls
              </p>
            </div>
            
            <div className="w-64 shrink-0">
              <div className="flex justify-between text-xs font-mono-data mb-1">
                <span className="text-slate-500 uppercase tracking-wider">Match Accuracy</span>
                <span className={`${results.accuracy > 95 ? 'text-emerald-400' : results.accuracy > 80 ? 'text-[#ff8000]' : 'text-rose-400'}`}>
                  {parseFloat(results.accuracy.toFixed(2))}%
                </span>
              </div>
              <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${results.accuracy > 95 ? 'bg-emerald-500' : results.accuracy > 80 ? 'bg-[#ff8000]' : 'bg-rose-500'}`}
                  style={{ width: `${results.accuracy}%` }}
                />
              </div>
            </div>
          </header>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            <div className="xl:col-span-7 flex flex-col space-y-4">
              <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-1">Simulation</h3>
              <Visualizer config={config} results={results} />
              
              <div className="space-y-4 mt-2">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-1 text-[9px] uppercase font-bold tracking-wider text-slate-500">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-yellow-500/20 border border-yellow-500 rounded-sm"></div> Physical Wall
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border-2 border-blue-500 rounded-sm"></div> Fit Content
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border border-red-600 rounded-sm"></div> Fill Content
                  </div>
                </div>
                
                <p className="text-[9px] text-slate-500/60 leading-relaxed font-medium px-1 max-w-2xl">
                  Disclaimer: This tool is for reference only. The creator cannot be held responsible for calculation errors. 
                  All results should be verified by a qualified professional before implementation.
                </p>
              </div>
            </div>
            
            <div className="xl:col-span-5 flex flex-col space-y-4">
              <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-1">Metrics Dashboard</h3>
              <MetricsGrid results={results} config={config} setConfig={setConfig} />
            </div>
          </div>

          <footer className="mt-auto pt-8 border-t border-slate-800 flex flex-col gap-6 pb-4">
            {/* Coffee Link Box Moved Above Dev Log */}
            <div className="flex justify-start">
              <a 
                href="https://buymeacoffee.com/hartleyvideo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-[10px] font-bold text-slate-500 hover:text-[#ff8000] transition-all uppercase tracking-[0.12em] group p-3 bg-slate-900/50 border border-slate-800 rounded-xl hover:border-[#ff8000]/20 transition-colors w-full sm:max-w-md"
              >
                <span className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center group-hover:bg-[#ff8000]/20 text-[#ff8000] border border-[#ff8000]/20 transition-all shrink-0">☕</span>
                <div className="leading-tight">
                  <span className="text-slate-500 group-hover:text-[#ff8000] transition-colors">Find this tool useful?</span> <br/>
                  <span className="text-[#ff8000] opacity-80 group-hover:opacity-100 transition-opacity">support my work by buying me a coffee</span>
                </div>
              </a>
            </div>

            <div className="w-full">
              <Changelog />
            </div>
          </footer>
        </main>
      </div>
      <CookieBanner />
    </div>
  );
};

export default App;
